import { TaxonomyType } from '@revelio/data-access';

import { GEOGRAPHY_TAXONOMY_TREE_LEVELS } from '../../../next/CustomDash/filters/utils/taxonomy-tree-levels';
import { sortUnspecifiedNodesLast } from '../utils/unspecifiedNodes';
import { type UseTaxonomyTreeResult, useTaxonomyTree } from './useTaxonomyTree';
import { useTaxonomyLatestVersion } from './useTaxonomyVersions';

/**
 * The geography taxonomy tree, at the version `taxonomyLatestVersion`
 * advertises. Omitting the version — what every geography fetch used to do —
 * does not mean "latest" to the backend, it means `1.0.0`.
 *
 * Every geography fetch goes through here rather than calling `useTaxonomyTree`
 * directly, because the version has to be the SAME one everywhere: a differing
 * version is a differing cache key, and two geography trees at two versions can
 * disagree on level tags (the `city_core` → `county` rename), which re-keys a
 * selection's `levelId` under the filter engine. The levels are fixed here for
 * the same reason — every consumer asked for the same four.
 */
export const useGeographyTaxonomyTree = ({
  taxonomyId,
  pause = false,
  priority,
}: {
  taxonomyId?: number;
  pause?: boolean;
  priority?: 'high' | 'low';
}): UseTaxonomyTreeResult => {
  const {
    version,
    hasData: hasVersion,
    error: versionError,
  } = useTaxonomyLatestVersion({
    taxonomyType: TaxonomyType.Geography,
    pause,
  });
  // An unresolvable version falls back to an unversioned fetch rather than
  // holding the tree forever — geography filters degrade to the old `1.0.0`
  // tree instead of never rendering.
  const isVersionSettled = hasVersion || versionError !== undefined;

  const result = useTaxonomyTree({
    taxonomyType: TaxonomyType.Geography,
    taxonomyId,
    taxonomyVersion: version,
    levels: GEOGRAPHY_TAXONOMY_TREE_LEVELS,
    pause: pause || !isVersionSettled,
    priority,
  });

  return {
    ...result,
    treeData: sortUnspecifiedNodesLast(result.treeData),
    // A paused `useTaxonomyTree` reports `fetching: false`, so without this the
    // version round-trip reads as settled-with-no-tree — and consumers gate on
    // `fetching` to hold a request back until the real levels land. A false
    // settle there sends the request keyed off their fallback levels.
    fetching: result.fetching || (!pause && !isVersionSettled),
  };
};
